<template>
    <div v-if="loading" class="state-changed w-full h-20 fixed border-0 top-0 left-0 z-50"></div>
    
    <div class="clear-both"></div>

    <slot v-if="!isLoadingMetircs && !refreshing && metricsTable && metricsTable.data && metricsTable.data.length>1">
        <transition name="fade">
            <section class="text-textOnBkg dark:text-dark.textOnBkg" :class="{'mt-4': period !== 'daily'}">
                <hr v-if="period !== 'daily'" class="border-t border-sepBorder dark:border-dark.sepBorder mb-4" />
                <h3 class="font-bold text-lg sm:text-2xl m-2 float-left">{{tableTitle}}
                <small v-if="period == 'daily'" class="mt-2 font-light text-sm ml-3">*{{ $t('earnings_difference_notice') }}</small>
                </h3>
                <div class="float-right m-2 mt-0 relative">
                    <!-- Show/Hide Button -->
                    <button @click="showColumnDropdown = !showColumnDropdown" class="bg-mazUIBg text-mazUiText dark:bg-dark.mazUIBg dark:text-dark.mazUiText rounded-xl cursor-pointer p-2.5 px-3 border border-sepBorder dark:border-dark.mazUIBg text-center whitespace-nowrap text-xs ml-2">
                        {{ $t('show_hide') }} <font-awesome-icon :icon="['fas', 'angle-down']" />
                    </button>
                    <!-- Show/Hide Dropdown -->
                    <div v-if="showColumnDropdown" class="absolute z-10 bg-mazUIBg text-mazUiText dark:bg-dark.mazUIBg dark:text-dark.mazUiText rounded-lg shadow-lg p-2 mt-1 right-32">
                        <div class="flex items-center mb-1 border-b border-sepBorder dark:border-dark.sepBorder pb-1">
                            <input type="checkbox" id="showAll" :checked="allColumnsVisible" @change="toggleAllColumns" class="mr-2" />
                            <label for="showAll" class="cursor-pointer">{{ $t('show_all') }}</label>
                        </div>
                        <div v-for="column in togglableColumns" :key="column.name" class="flex items-center">
                            <input type="checkbox" :id="column.name" v-model="columnVisibility[column.name]" class="mr-2" :disabled="column.disabled" />
                            <label :for="column.name" class="whitespace-nowrap">{{ column.label }}</label>
                        </div>
                    </div>
                    <button class="bg-button text-buttonText ml-2 rounded-lg p-1.5 px-3 -mt-1 cursor-pointer" @click="exportToCSV">
                        <span ref="exportLoading" class="hidden"><font-awesome-icon icon="fas fa-spinner" class="animate-spin mr-2" /></span>{{ $t('export_to_csv') }}
                    </button>
                </div>
                <div class="clear-both"></div>

                <div class="overflow-y-auto max-h-[32rem] border border-sepBorder dark:border-dark.sepBorder rounded-lg w-full">
                <div @click="resetComments()" v-if="hideCom && editable == true" class="z-10 absolute w-full h-full bg-black bg-opacity-20"></div>
                <table ref="tableRef" class="w-full text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800" :style="{ 'max-height': period === 'daily' ? '560px' : '450px' }">
                    <thead ref="theadRef" class="sticky top-0 bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-200 border-b border-sepBorder dark:border-dark.sepBorder">
                    <tr class="text-sm">
                        <th class="whitespace-nowrap date z-9 border-r border-sepBorder dark:border-dark.sepBorder">{{ $t('date') }}</th>
                        <th v-if="columnVisibility.creators && selectedModel == null" class="whitespace-nowrap px-2 border-r border-sepBorder dark:border-dark.sepBorder">{{ $t('Creators') }}</th>
                        <th v-if="columnVisibility.activeFans" class="whitespace-nowrap border-r border-sepBorder dark:border-dark.sepBorder"><div class="mx-1.5">{{ $t('active') + ' ' + $t('fans') }}</div></th>
                        <th v-if="columnVisibility.change" class="whitespace-nowrap px-2 border-r border-sepBorder dark:border-dark.sepBorder">{{ $t('change') }}</th>
                        <th v-if="columnVisibility.expiredFans" class="whitespace-nowrap border-r border-sepBorder dark:border-dark.sepBorder"><div class="mx-1">{{ $t('expired_fans') }}</div></th>
                        <th v-if="columnVisibility.newFans" class="whitespace-nowrap px-2 border-r border-sepBorder dark:border-dark.sepBorder">{{ $t('new') + ' ' + $t('fans') }}</th>
                        <th v-if="columnVisibility.likes" class="whitespace-nowrap border-r border-sepBorder dark:border-dark.sepBorder">{{ $t('likes') }}</th>
                        <th v-if="columnVisibility.renew" class="whitespace-nowrap border-r border-sepBorder dark:border-dark.sepBorder"><div class="mx-1">{{ $t('renew') }}</div></th>
                        <th v-if="columnVisibility.messageCount" class="whitespace-nowrap border-r border-sepBorder dark:border-dark.sepBorder">{{ $t('message_count') }}</th>
                        <th v-if="columnVisibility.overallSales" class="whitespace-nowrap border-r border-sepBorder dark:border-dark.sepBorder"><div class="mx-2">{{ $t('overall_sales') }}</div></th>
                        <th v-if="columnVisibility.subscriptionSales" class="border-r whitespace-nowrap border-sepBorder dark:border-dark.sepBorder font-semibold"><div class="mx-2">{{ $t('subscription_sales') }}</div></th>
                        <th v-if="columnVisibility.fansGrowthCreator && selectedModel == null" class="average_fans whitespace-nowrap border-r border-sepBorder dark:border-dark.sepBorder">{{ $t('fans_growth_cretor') }}</th>
                        <th v-if="columnVisibility.earningsCreator && selectedModel == null" class="average_sales whitespace-nowrap border-r border-sepBorder dark:border-dark.sepBorder">{{ $t('earnings_creator') }}</th>
                    </tr>
                    </thead>
                    <tbody ref="tbodyRef" v-if="metricsTable" @scroll="syncScroll">
                    <slot v-for="(metric, index) in metricsTable.data" :key="index">
                        <slot v-if="metric.models.free">
                        <tr v-if="index > 0 || (period == 'daily' && (index == 0 && isDate1BeforeDate2(metric.forDate, dateFrom)))" v-bind:class="{'bg-tableRow1 dark:bg-dark.tableRow1 dark:text-dark.tableText': index % 2 == 0}">
                            <td class="border border-sepBorder dark:border-dark.sepBorder z-9 whitespace-nowrap" :title="metric.dayName" v-bind:class="{'bg-tableRow1 dark:bg-dark.tableRow1 dark:text-dark.tableText': index % 2 == 0, 'bg-tableBG dark:bg-dark.tableBG dark:text-dark.tableText': index % 2 == 1}">{{metric.forDate}}</td>
                            <td v-if="metric.models.modelNames && columnVisibility.creators && selectedModel == null" class="border border-sepBorder dark:border-dark.sepBorder" @mouseover="hoverModelsIndex = index" @mouseleave="hoverModelsIndex = null">
                            {{ addSpace(metric.models?.modelNames?.length) }}
                            <div v-if="hoverModelsIndex == index" class="absolute max-h-60 overflow-y-scroll z-30 bg-gray-200 text-panelsText -mt-7 ml-20 p-2 px-5 text-sm rounded-lg">
                                <ol><li v-for="(model, mIndex) in metric.models.modelNames" :key="mIndex">{{model}}</li></ol>
                            </div>
                            </td>
                            <td v-if="columnVisibility.activeFans" class="border border-sepBorder dark:border-dark.sepBorder">
                            <metricsTableblock
                                v-if="metric.models.free"
                                @changedData="changedData = $event"
                                @changedComment="changedComment = $event"
                                :editable="editable"
                                :optionPosition="optionPosition"
                                :column="'activeFans'"
                                :selectedModel="selectedModel"
                                :date="metric.forDate"
                                :commentData="metric.models.free.comments.fans_active"
                                :displayComments="displayComments"
                                :editData="metric.models.free.fans_active"
                            />
                            </td>
                            <td v-if="columnVisibility.change && index == 0 && isDate1BeforeDate2(metric.forDate, dateFrom)" class="border border-sepBorder dark:border-dark.sepBorder">0</td>
                            <td v-if="columnVisibility.change && !(index == 0 && isDate1BeforeDate2(metric.forDate, dateFrom))" class="border border-sepBorder dark:border-dark.sepBorder" 
                            v-bind:class="{'text-growth': (metric.models.free.fans_active - metricsTable.data[index-1].models.free.fans_active + metric.models.free.fans_expired - metricsTable.data[index-1].models.free.fans_expired) - (metric.models.free.fans_expired - metricsTable.data[index-1].models.free.fans_expired) > 0,
                                            'text-decline': (metric.models.free.fans_active - metricsTable.data[index-1].models.free.fans_active + metric.models.free.fans_expired - metricsTable.data[index-1].models.free.fans_expired) - (metric.models.free.fans_expired - metricsTable.data[index-1].models.free.fans_expired) < 0}">
                            {{ addSpace((metric.models.free.fans_active - metricsTable.data[index-1].models.free.fans_active + metric.models.free.fans_expired - metricsTable.data[index-1].models.free.fans_expired) - (metric.models.free.fans_expired - metricsTable.data[index-1].models.free.fans_expired)) }}
                            </td>
                            <td v-if="columnVisibility.expiredFans && index == 0 && isDate1BeforeDate2(metric.forDate, dateFrom)" class="border border-sepBorder dark:border-dark.sepBorder">0</td>
                            <td v-if="columnVisibility.expiredFans && !(index == 0 && isDate1BeforeDate2(metric.forDate, dateFrom))" class="border border-sepBorder dark:border-dark.sepBorder">
                            <metricsTableblock
                                @changedData="changedData = $event"
                                @changedComment="changedComment = $event"
                                :editable="editable"
                                :optionPosition="optionPosition"
                                :column="'expiredFans'"
                                :selectedModel="selectedModel"
                                :date="metric.forDate"
                                :commentData="metric.models.free.comments.fans_expired"
                                :displayComments="displayComments"
                                :editData="metric.models.free.fans_expired"
                            />
                            </td>
                            <td v-if="columnVisibility.newFans && index == 0 && isDate1BeforeDate2(metric.forDate, dateFrom)" class="border border-sepBorder dark:border-dark.sepBorder">0</td>
                            <td v-if="columnVisibility.newFans && !(index == 0 && isDate1BeforeDate2(metric.forDate, dateFrom))" class="border border-sepBorder dark:border-dark.sepBorder" 
                            v-bind:class="{'text-growth': (metric.models.free.fans_active - metricsTable.data[index-1].models.free.fans_active + metric.models.free.fans_expired - metricsTable.data[index-1].models.free.fans_expired) > 0,
                                            'text-decline': (metric.models.free.fans_active - metricsTable.data[index-1].models.free.fans_active + metric.models.free.fans_expired - metricsTable.data[index-1].models.free.fans_expired) < 0}">
                            {{ addSpace(metric.models.free.fans_active - metricsTable.data[index-1].models.free.fans_active + metric.models.free.fans_expired - metricsTable.data[index-1].models.free.fans_expired) }}
                            </td>
                            <td v-if="columnVisibility.likes && index == 0 && isDate1BeforeDate2(metric.forDate, dateFrom)" class="border border-sepBorder dark:border-dark.sepBorder whitespace-nowrap">0</td>
                            <td v-if="columnVisibility.likes && !(index == 0 && isDate1BeforeDate2(metric.forDate, dateFrom))" class="border border-sepBorder dark:border-dark.sepBorder whitespace-nowrap">
                                {{ addSpace(parseInt(metric.models.free.likes)) }}
                            </td>
                            <td v-if="columnVisibility.renew && metric.models.free.renewOn" class="border border-sepBorder dark:border-dark.sepBorder" v-bind:class="{'border-2 border-yellow-200': hideCom && editable == true && showOptions.index == 'ren' + index, 'border-2 border-decline': displayComments && metric.models.free.comments && metric.models.paid.comments.renewOn.length}">
                            <metricsTableblock
                                @changedData="changedData = $event"
                                @changedComment="changedComment = $event"
                                :editable="editable"
                                :optionPosition="optionPosition"
                                :column="'renewOn'"
                                :selectedModel="selectedModel"
                                :date="metric.forDate"
                                :commentData="metric.models.free.comments.renewOn"
                                :displayComments="displayComments"
                                :editData="metric.models.free.renewOn"
                            />
                            </td>
                            <td v-else-if="columnVisibility.renew" class="border border-sepBorder dark:border-dark.sepBorder" v-bind:class="{'border-2 border-yellow-200': hideCom && editable == true && showOptions.index == 'ren' + index, 'border-2 border-decline': displayComments && metric.models.paid.comments && metric.models.paid.comments.renewOn.length}">
                            <metricsTableblock
                                @changedData="changedData = $event"
                                @changedComment="changedComment = $event"
                                :editable="editable"
                                :optionPosition="optionPosition"
                                :column="'renewOn'"
                                :selectedModel="selectedModel"
                                :date="metric.forDate"
                                :commentData="metric.models.paid.comments.renewOn"
                                :displayComments="displayComments"
                                :editData="metric.models.paid.renewOn"
                            />
                            </td>
                            <td v-if="columnVisibility.messageCount" class="border border-sepBorder dark:border-dark.sepBorder">
                            <metricsTableblock
                                v-if="metric.models.free.comments"
                                @changedData="changedData = $event"
                                @changedComment="changedComment = $event"
                                :editable="editable"
                                :optionPosition="optionPosition"
                                :column="'messages'"
                                :selectedModel="selectedModel"
                                :date="metric.forDate"
                                :commentData="metric.models.free.comments.messages"
                                :displayComments="displayComments"
                                :editData="metric.models.free.messages"
                            />
                            </td>
                            <td v-if="columnVisibility.overallSales" class="border border-sepBorder dark:border-dark.sepBorder">
                            <metricsTableblock
                                @changedData="changedData = $event"
                                @changedComment="changedComment = $event"
                                :editable="editable"
                                :optionPosition="optionPosition"
                                :column="'transactionSum'"
                                :signSymbol="'$'"
                                :selectedModel="selectedModel"
                                :date="metric.forDate"
                                :commentData="metric.models.free.comments.transactionSum"
                                :displayComments="displayComments"
                                :editData="(metric.models.free.transactionSum - metric.models.free.reverseSum).toFixed(2)"
                            />
                            </td>
                            <td v-if="columnVisibility.subscriptionSales" class="border border-sepBorder dark:border-dark.sepBorder">
                            <metricsTableblock
                                @changedData="changedData = $event"
                                @changedComment="changedComment = $event"
                                :editable="editable"
                                :optionPosition="optionPosition"
                                :column="'subsEarnings'"
                                :signSymbol="'$'"
                                :selectedModel="selectedModel"
                                :date="metric.forDate"
                                :commentData="metric.models.free.comments.subsEarnings"
                                :displayComments="displayComments"
                                :editData="(metric.models.free.subsEarnings ?? 0).toFixed(2)"
                            />
                            </td>
                            <td v-if="columnVisibility.fansGrowthCreator && selectedModel == null && index == 0 && isDate1BeforeDate2(metric.forDate, dateFrom)" class="border-sepBorder dark:border-dark.sepBorder" style="border-left: 2px solid black;">0</td>
                            <td v-if="columnVisibility.fansGrowthCreator && selectedModel == null && !(index == 0 && isDate1BeforeDate2(metric.forDate, dateFrom))" class="border border-sepBorder dark:border-dark.sepBorder" 
                            v-bind:class="{'text-growth': (metric.models.free.fans_active - metricsTable.data[index-1].models.free.fans_active + metric.models.free.fans_expired - metricsTable.data[index-1].models.free.fans_expired) - (metric.models.free.fans_expired - metricsTable.data[index-1].models.free.fans_expired) > 0, 
                                            'text-decline': (metric.models.free.fans_active - metricsTable.data[index-1].models.free.fans_active + metric.models.free.fans_expired - metricsTable.data[index-1].models.free.fans_expired) - (metric.models.free.fans_expired - metricsTable.data[index-1].models.free.fans_expired) < 0}">
                            {{ (((metric.models.free.fans_active - metricsTable.data[index-1].models.free.fans_active + metric.models.free.fans_expired - metricsTable.data[index-1].models.free.fans_expired) - (metric.models.free.fans_expired - metricsTable.data[index-1].models.free.fans_expired)) / metric.models.modelNames.length).toFixed(2) }}
                            </td>
                            <td v-if="columnVisibility.earningsCreator && selectedModel == null && index == 0 && isDate1BeforeDate2(metric.forDate, dateFrom)" class="border border-sepBorder dark:border-dark.sepBorder">0</td>
                            <td v-if="columnVisibility.earningsCreator && selectedModel == null && !(index == 0 && isDate1BeforeDate2(metric.forDate, dateFrom))" class="border border-sepBorder dark:border-dark.sepBorder" 
                            v-bind:class="{'text-decline': ((metric.models.free.transactionSum + metric.models.paid.transactionSum) / metric.models.modelNames.length) < ((metricsTable.data[index-1].models.free.transactionSum + metricsTable.data[index-1].models.paid.transactionSum) / metricsTable.data[index-1].models.modelNames.length),
                                            'text-growth': ((metric.models.free.transactionSum + metric.models.paid.transactionSum) / metric.models.modelNames.length) > ((metricsTable.data[index-1].models.free.transactionSum + metricsTable.data[index-1].models.paid.transactionSum) / metricsTable.data[index-1].models.modelNames.length)}">
                            $ {{ addSpace(parseFloat((metric.models.free.transactionSum + metric.models.paid.transactionSum) / metric.models.modelNames.length).toFixed(2)) }}
                            </td>
                        </tr>
                        </slot>
                    </slot>
                    <summary-line
                    v-if="metricsTable && metricsTable.data && metricsTable.data.length > 0 && metricsTable.summary"
                    :selectedPlatform="selectedPlatform"
                    :selectedModel="selectedModel"
                    :metricsData="metricsTable.summary"
                    :displayColumns="columnVisibility"
                    />
                    <average-line
                    v-if="metricsTable && metricsTable.data && metricsTable.data.length > 0 && metricsTable.summary"
                    :selectedPlatform="selectedPlatform"
                    :selectedModel="selectedModel"
                    :metricsData="metricsTable.average"
                    :displayColumns="columnVisibility"
                    />
                    </tbody>
                </table>
                </div>
            </section>
        </transition>
    </slot>

    <slot v-else-if="loading">
        <transition name="fade">
            <section>
                <h3 class="text-textOnBkg dark:text-dark.textOnBkg font-bold text-lg sm:text-2xl m-2">{{tableTitle}}</h3>
                <div class="w-full bg-tableBG dark:bg-dark.tableBG text-textOnBkg dark:text-dark.textOnBkg rounded text-center py-60 border border-sepBorder dark:border-dark.sepBorder ml-1" :class="{'py-60':period == 'daily','py-52': period != 'daily'}"><font-awesome-icon icon="fas fa-spinner" class="animate-spin" /> Loading</div>
            </section>
        </transition>
    </slot>
    <slot v-else>
        <transition name="fade">
            <section>
                <h3 class="text-textOnBkg dark:text-dark.textOnBkg font-bold text-lg sm:text-2xl m-2">{{tableTitle}}</h3>
                <div class="w-full bg-tableBG dark:bg-dark.tableBG text-textOnBkg dark:text-dark.textOnBkg rounded text-center py-60 border border-sepBorder dark:border-dark.sepBorder ml-1" :class="{'py-60':period == 'daily','py-52': period != 'daily'}">No data</div>
            </section>
        </transition>
    </slot>
</template>

<script setup>
    import metricsComment from "@/components/metrics/metricsComment.vue"
    import metricsTableblock from "@/components/metrics/metricsAddComment.vue"
    import summaryLine from "@/components/metrics/metricsSummaryLine.vue"
    import averageLine from "@/components/metrics/metricsAverageLine.vue"
    import { get_metrics, updateMetrics_api_call } from "@/api/api.js"
</script>

<script>
    export default {
        props: ['refreshing','period', 'tableTitle', 'summary','hideF','changedComment','changedData','selectedModelN','selectedPlatform','selectedGroupN','selectedModel','selectedGroup','dateFrom','dateTo','permissions'],
        components() {
            metricsComment,
            metricsTableblock,
            summaryLine,
            averageLine
        },
        data() {
            return {
                metricsTable: [],
                hideCom:false,
                eFansF: {editing:null,value:null},
                exFansF: {editing:null,value:null},
                eFansP: {editing:null,value:null},
                exFansP: {editing:null,value:null},
                eDMF: {editing:null,value:null},
                eDMP: {editing:null,value:null},
                eEarnF: {editing:null,value:null},
                eEarnP: {editing:null,value:null},
                eRen: {editing:null,value:null},
                sbP: {editing:null,value:null},
                loading: false,
                displayComments:true,
                displayRenewOn: true,
                displaySubs: true,
                editable: false,
                hoverModelsIndex:null,
                hoverCommentIndex:null,
                mounted: false,
                addNewLine: false,
                add: {date: null, free: {fans:null,massDM:null,trans:null}, paid: {fans:null,massDM:null,trans:null}},
                submited: false,
                showOptions:{index:0,options:1},
                optionPosition:{x:null,y:null},
                displayColumns: {free:false,paid:false},
                isLoadingMetircs:false,
                editMState: null,
                hideFState: null,
                showColumnDropdown: false,
                columnVisibility: {
                    creators: true,
                    activeFans: true,
                    change: true,
                    expiredFans: true,
                    newFans: true,
                    likes: true,
                    renew: true, // Synced with displayRenewOn initially
                    messageCount: true,
                    overallSales: true,
                    subscriptionSales: true, // Synced with displaySubs initially
                    fansGrowthCreator: true,
                    earningsCreator: true
                }
            }
        },
        computed: {
            allColumnsVisible() {
                return Object.values(this.columnVisibility).every((visible) => visible);
            },
            togglableColumns() {
                return [
                    { name: 'creators', label: this.$t('Creators'), disabled: false },
                    { name: 'activeFans', label: this.$t('active') + ' ' + this.$t('fans'), disabled: false },
                    { name: 'change', label: this.$t('change'), disabled: false },
                    { name: 'expiredFans', label: this.$t('expired_fans'), disabled: false },
                    { name: 'newFans', label: this.$t('new') + ' ' + this.$t('fans'), disabled: false },
                    { name: 'likes', label: this.$t('likes'), disabled: false },
                    { name: 'renew', label: this.$t('renew'), disabled: false },
                    { name: 'messageCount', label: this.$t('message_count'), disabled: false },
                    { name: 'overallSales', label: this.$t('overall_sales'), disabled: false },
                    { name: 'subscriptionSales', label: this.$t('subscription_sales'), disabled: false },
                    { name: 'fansGrowthCreator', label: this.$t('fans_growth_cretor'), disabled: false },
                    { name: 'earningsCreator', label: this.$t('earnings_creator'), disabled: false }
                ];
            }
        },
        watch: {
            changedData: function() {
                this.metricsTable.data[this.changedData[1]].models[this.changedData[2]][this.changedData[3]] = this.changedData[0];
            },
            changedComment: function() {
                this.metricsTable.data[this.changedComment[1]].models[this.changedComment[2]].comments[this.changedComment[3]] = this.changedComment[0];
            },
            hideF: function() {
                this.hideCom = this.hideF[1];
            },
            refreshing: function (n,o) {
                this.metricsTable = [];
                this.getMetricsData();
            },
            selectedModel: function(newVal, oldVal) { // watch it
                if ((newVal === null && oldVal === undefined) || !this.mounted) {
                    return;
                }
                this.metricsTable = [];
                this.getMetricsData();
            },
            dateFrom: function(newVal, oldVal) { // watch it
                if ((newVal === null && oldVal === undefined) || !this.mounted) {
                    return;
                }
                this.getMetricsData();
            },
            dateTo: function(newVal, oldVal) { // watch it
                if ((newVal === null && oldVal === undefined) || !this.mounted) {
                    return;
                }
                this.getMetricsData();
            },
            selectedGroup: function(newVal, oldVal) { // watch it
                if ((newVal === null && oldVal === undefined) || (newVal === 0 && oldVal === undefined) || !this.mounted) {
                    return;
                }
                this.getMetricsData(null);
            },
            selectedPlatform: function(newVal,oldVal) {
                if ((newVal === null && oldVal === undefined) || !this.mounted) {
                    return;
                }
                this.getMetricsData();
            }
        },
        methods: {
            toggleAllColumns(event) {
                const isChecked = event.target.checked;
                for (const column in this.columnVisibility) {
                    this.columnVisibility[column] = isChecked;
                }
            },
            updateHideF(newHideF) {
                this.hideFState = newHideF; // Update the reactive state
            },
            updateEditM(newEditM) {
                this.editMState = newEditM; // Update the reactive state
            },
            getIconClass(condition) {
                return condition ? 'fas fa-check-square' : 'fas fa-square';
            },
            plusMinusIcon(condition) {
                return condition ? 'fa-circle-minus': 'fa-circle-plus';
            },
            exportToCSV() {
                this.$refs.exportLoading.classList.remove('hidden');

                setTimeout(() => {
                    const table = this.$refs.tableRef;
                    const data = [];

                    const rows = table.querySelectorAll('tr');
                    rows.forEach(row => {
                        const rowData = [];
                        row.querySelectorAll('td, th').forEach(cell => {
                            rowData.push(cell.innerText);
                        });
                        data.push(rowData.join(','));
                    });

                    // Create a CSV content string
                    const csvContent = 'data:text/csv;charset=utf-8,' + data.join('\n');

                    const encodedUri = encodeURI(csvContent);
                    const link = document.createElement('a');
                    link.setAttribute('href', encodedUri);
                    let filename = this.period + "_metrics_" + this.$moment().format('YYYY-MM-DD') + ".csv";
                    link.setAttribute('download', filename);

                    setTimeout(() => {
                        this.$refs.exportLoading.classList.add('hidden');
                        link.click();
                    }, 1000);
                }, 2000);
            },
            syncScroll() {
                const thead = this.$refs.theadRef;
                const tbody = this.$refs.tbodyRef;
                if (thead && tbody) {
                    thead.scrollLeft = tbody.scrollLeft;
                }
            },
            async getMetricsData() {
                if (this.isLoadingMetircs) {return;}
                this.isLoadingMetircs = true;
                try {
                    this.loading = true;
                    this.metricsTable = await get_metrics(this.$cookies.get('userToken'),this.$cookies.get('c_id'),this.selectedGroup,this.selectedModel,this.$moment(this.dateFrom).format('YYYY-MM-DD'),this.$moment(this.dateTo).format('YYYY-MM-DD'), this.period, this.summary);
                } catch (error) {
                    console.log(error);
                } finally {
                    this.isLoadingMetircs = false;
                    this.loading = false;
                }
            },
            isDate1BeforeDate2(date1, date2) {
                let formattedDate1 = new Date(date1.split('.').reverse().join('-'));
                let formattedDate2 = new Date(date2);

                formattedDate1.setHours(0, 0, 0, 0);
                formattedDate2.setHours(0, 0, 0, 0);

                return formattedDate1.getTime() >= formattedDate2.getTime();
            },
            openReverseAlert(alertMessage) {
                if (alertMessage>0) {
                    alert("Reverse summary for this day: $ " + alertMessage);
                }
            },
            resetComments() {
                this.hideCom = false;
            },
            /*
            showMetricsOptions(ind,index) {
                if (this.selectedModel != null && index!=this.metricsTable.data.length-1) {
                    this.hideCom = true;
                    this.showOptions.options = 1;
                    //this.hideF = true;
                    this.showOptions.index = ind
                    this.optionPosition.x = pointerX+50;
                }
            },*/
            async saveNew() {
                this.submited = true
                if (this.add.date)  {
                    let dateVar = this.$moment(this.add.date).format('YYYY-MM-DD');
                    const response = await updateMetrics_api_call({                    
                        action: "addMetrics",
                        Model_pair_id: this.selectedModel,
                        addData: this.add,
                        dataDate: dateVar,
                        userToken: this.$cookies.get('userToken'),
                        companyID: this.$cookies.get('c_id')
                    });

                    if (response != 'duplicate') {
                        this.addNewLine = false;
                        this.metricsTable.push({
                            date: this.$moment(this.add.date).format('DD.MM.YYYY'), 
                            free: {
                                fans_active: response.free.fans,
                                fans_expired: 0,
                                messages: response.free.massDM,
                                transactionSum: parseFloat(response.free.trans.replace(",",".")),
                            },
                            paid: {
                                fans_active: response.paid.fans,
                                fans_expired: 0,
                                messages: response.paid.massDM,
                                transactionSum: parseFloat(response.paid.trans.replace(",",".")),
                            }
                        });
                        this.add = {date: null, free: {fans:null,massDM:null,trans:null}, paid: {fans:null,massDM:null,trans:null}};
                        this.submited = false
                    } else {
                        alert("Data for this date already exists");
                    }
                }
            },
            addSpace(nStr) {
                nStr += '';
                var x = nStr.split('.');
                var x1 = x[0];
                var x2 = x.length > 1 ? '.' + x[1] : '';
                var rgx = /(\d+)(\d{3})/;
                while (rgx.test(x1)) {
                    x1 = x1.replace(rgx, '$1' + " " + '$2');
                }
                return x1 + x2;
            },
            updateMetrics(which,index,newVal) {
                newVal = parseFloat(newVal.replace(",","."));
                updateMetrics_api_call({                    
                    action: "updateMetrics",
                    Model_pair_id: this.selectedModel,
                    newValue: newVal,
                    column: which,
                    date: this.metricsTable[index].date,
                    userToken: this.$cookies.get('userToken'),
                    companyID: this.$cookies.get('c_id')
                });
            }
        },
        mounted() {
            if (this.permissions 
                && this.$getPermission(this.permissions,2) 
                && this.$getPermission(this.permissions,2).is_granted
                && this.period == 'daily') 
            {
                this.editable = true;
            }
            this.mounted = true;
            if (!(this.metricsTable.data && this.metricsTable.data.length>1)) { 
                setTimeout(() => {
                    this.getMetricsData();
                }, 500);
            }
        }
    }
</script>

<style scoped >
    td, th {
        padding: 0.4rem;
    }
    td {
        text-align: center;
    }
    .fade-enter-active, .fade-leave-active {
        transition: opacity 0.5s ease;
    }
    .fade-enter, .fade-leave-to /* Starting and ending state for fade-out */ {
        opacity: 0;
    }
</style>